import React, { useState, useMemo, useCallback, useEffect } from "react";
import Modal from "react-modal";
import axios from "axios";
import "../styles/LeftPanelPersonal.css";

Modal.setAppElement("#root");

const StatusChangeModal = ({
  isOpen,
  onClose,
  userId,
  currentStatus,
  newStatus,
  newStatusLabel,
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [statusReasons, setStatusReasons] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [createNewProfile, setCreateNewProfile] = useState(false);
  const [positionMode, setPositionMode] = useState("none");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [positions, setPositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const segments = useMemo(
    () => ["Candidate", "Future Candidate", "Prospect", "Inactive"],
    []
  );
  const canCreateNewProfile =
    [2, 5].includes(Number(currentStatus)) &&
    [1, 3].includes(Number(newStatus));
  const orgUnitNames = useMemo(
    () =>
      new Map(
        orgUnits.map((orgUnit) => [String(orgUnit.ou_id), orgUnit.ou_name])
      ),
    [orgUnits]
  );
  const filteredPositions = useMemo(
    () =>
      selectedCategoryId
        ? positions.filter(
            (position) =>
              Number(position.p_category) === Number(selectedCategoryId)
          )
        : [],
    [positions, selectedCategoryId]
  );

  useEffect(() => {
    if (!isOpen || !canCreateNewProfile) {
      setCreateNewProfile(false);
      setPositionMode("none");
      setSelectedCategoryId("");
      setSelectedPositionId("");
    }
  }, [isOpen, canCreateNewProfile, currentStatus, newStatus]);

  useEffect(() => {
    const fetchStatusReasons = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/dropdowns/status-reasons`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setStatusReasons(res.data || []);
      } catch (err) {
        console.error("❌ Failed to load status reasons:", err);
      }
    };

    if (isOpen) {
      fetchStatusReasons();
    }
  }, [isOpen, API_BASE]);

  useEffect(() => {
    if (!isOpen || !canCreateNewProfile) return;

    const fetchPositions = async () => {
      setPositionsLoading(true);
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const [positionsResponse, categoriesResponse, orgUnitsResponse] = await Promise.all([
          axios.get(`${API_BASE}/api/positions`, { headers }),
          axios.get(`${API_BASE}/api/dropdowns/categories`, { headers }),
          axios.get(`${API_BASE}/api/dropdowns/org-units`, { headers }),
        ]);
        setPositions(
          (Array.isArray(positionsResponse.data)
            ? positionsResponse.data
            : []
          ).filter(
            (position) => Number(position.p_status) === 1
          )
        );
        setCategories(
          Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []
        );
        setOrgUnits(
          Array.isArray(orgUnitsResponse.data) ? orgUnitsResponse.data : []
        );
      } catch (err) {
        console.error("Failed to load active positions:", err);
        setPositions([]);
        setCategories([]);
        setOrgUnits([]);
      } finally {
        setPositionsLoading(false);
      }
    };

    fetchPositions();
  }, [isOpen, canCreateNewProfile, API_BASE]);

  const onFilePicked = (f) => {
    if (!f) return;
    setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    onFilePicked(f);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleSubmit = async () => {
    if (isSaving) return;
    if (!userId || !newStatus || !reason) {
      alert("Reason and target status are required.");
      return;
    }
    if (
      createNewProfile &&
      positionMode === "select" &&
      (!selectedCategoryId || !selectedPositionId)
    ) {
      alert("Please select a category and position for the new profile.");
      return;
    }

    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("userId", userId);
    fd.append("oldStatus", currentStatus);
    fd.append("newStatus", newStatus);
    fd.append("reason", reason); // sr_id
    fd.append("effectiveDate", effectiveDate);
    fd.append("note", note || "");
    fd.append("createNewProfile", createNewProfile ? "1" : "0");
    fd.append(
      "keepPosition",
      createNewProfile && positionMode === "keep" ? "1" : "0"
    );
    fd.append(
      "positionId",
      createNewProfile && positionMode === "select"
        ? selectedPositionId
        : ""
    );

    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/user/status-change?userId=${userId}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      onSuccess?.(response.data);
      onClose?.();
      // ✅ Automatically refresh page after successful change
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Error saving status change:", err);
      alert(err.response?.data?.message || "Save failed.");
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        if (!isSaving) onClose?.();
      }}
      shouldCloseOnEsc={!isSaving}
      shouldCloseOnOverlayClick={!isSaving}
      className="status-modal"
      overlayClassName="status-overlay"
    >
      {isSaving && (
        <div
          className="scm-saving-overlay"
          role="status"
          aria-live="assertive"
        >
          <span className="scm-saving-spinner" aria-hidden="true" />
          <strong>Saving status change...</strong>
          <span>Please wait while the profile is being updated.</span>
        </div>
      )}

      <div className="scm-seg">
        {segments.map((label) => (
          <div
            key={label}
            className={`scm-seg-item ${
              label === (newStatusLabel || "") ? "active" : ""
            }`}
            title={label}
          >
            {label}
          </div>
        ))}
      </div>

      <div className="scm-form">
        <div className="scm-field">
          <label className="scm-label">
            Reason for Change <span className="req">*</span>
          </label>
          <div className="scm-select-wrap">
            <select
              className="scm-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select Reason</option>
              {statusReasons.map((r) => (
                <option key={r.sr_id} value={r.sr_id}>
                  {r.sr_name}
                </option>
              ))}
            </select>
            <i className="fa fa-chevron-down scm-select-caret" />
          </div>
        </div>

        {canCreateNewProfile && (
          <div className="scm-field scm-clone-options">
            <label className="scm-checkbox-inline scm-clone-toggle">
              <input
                type="checkbox"
                checked={createNewProfile}
                onChange={(e) => {
                  const checked = e.target.checked;
                   setCreateNewProfile(checked);
                   if (!checked) {
                     setPositionMode("none");
                     setSelectedCategoryId("");
                     setSelectedPositionId("");
                  }
                }}
              />
              <span>Create New Profile (Clone)</span>
            </label>

            {createNewProfile && (
              <div className="scm-position-assignment">
                <div className="scm-position-title">
                  Position for the New Profile
                </div>
                <div className="scm-position-help">
                  Choose how the position and its tasks and training should be
                  assigned.
                </div>

                {[
                  {
                    value: "keep",
                    title: "Keep Current Position",
                    description:
                      "Copy the current position, category, organization, and work and study history.",
                  },
                  {
                    value: "select",
                    title: "Choose Another Position",
                    description:
                      "Assign a different active position and its related programs, and copy work and study history.",
                  },
                  {
                    value: "none",
                    title: "Candidate Re-apply in Job Page",
                    description:
                      "Create a profile without a position, copy work and study history, and let the candidate apply again from the Job Page.",
                  },
                ].map((choice) => (
                  <label
                    key={choice.value}
                    className={`scm-position-option ${
                      positionMode === choice.value ? "selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="clone-position-mode"
                      value={choice.value}
                      checked={positionMode === choice.value}
                       onChange={() => {
                         setPositionMode(choice.value);
                         if (choice.value !== "select") {
                           setSelectedCategoryId("");
                           setSelectedPositionId("");
                         }
                      }}
                    />
                    <span>
                      <strong>{choice.title}</strong>
                      <small>{choice.description}</small>
                    </span>
                  </label>
                 ))}

                {positionMode === "select" && (
                  <div className="scm-position-select-wrap">
                    <label htmlFor="clone-category-select">
                      Select Category <span className="req">*</span>
                    </label>
                    <select
                      id="clone-category-select"
                      className="scm-input"
                      value={selectedCategoryId}
                      onChange={(event) => {
                        setSelectedCategoryId(event.target.value);
                        setSelectedPositionId("");
                      }}
                      disabled={positionsLoading}
                    >
                      <option value="">
                        {positionsLoading
                          ? "Loading categories..."
                          : "Select a category"}
                      </option>
                      {categories.map((category) => (
                        <option key={category.c_id} value={category.c_id}>
                          {category.c_name}
                        </option>
                      ))}
                    </select>

                    <label htmlFor="clone-position-select">
                      Select Position <span className="req">*</span>
                    </label>
                    <select
                      id="clone-position-select"
                      className="scm-input"
                      value={selectedPositionId}
                      onChange={(event) =>
                        setSelectedPositionId(event.target.value)
                      }
                      disabled={positionsLoading || !selectedCategoryId}
                    >
                      <option value="">
                        {positionsLoading
                          ? "Loading positions..."
                          : !selectedCategoryId
                            ? "Select a category first"
                            : filteredPositions.length
                              ? "Select an active position"
                              : "No active positions in this category"}
                      </option>
                      {filteredPositions.map((position) => (
                        <option key={position.p_id} value={position.p_id}>
                          {position.p_position_number
                            ? `${position.p_position_number} - `
                            : ""}
                          {position.p_position_title}
                          {orgUnitNames.get(String(position.p_org_unit))
                            ? ` - ${orgUnitNames.get(
                                String(position.p_org_unit)
                              )}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="scm-field">
          <label className="scm-label">
            Effective As Of <span className="req">*</span>
          </label>
          <input
            type="date"
            className="scm-input"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            required
          />
        </div>

        <div className="scm-field">
          <label className="scm-label">Status Change Note</label>
          <textarea
            className="scm-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder=""
          />
        </div>

        <div className="scm-field">
          <div className="scm-docs-title">Documents</div>

          <div
            className={`scm-dropzone ${dragOver ? "drag" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="scm-drop-contents">
              <i className="fa fa-upload scm-upload-icon" />
              <div className="scm-drop-text">
                Drag & Drop your files or Browse
              </div>
              {file && <div className="scm-file-name">{file.name}</div>}
            </div>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.msg"
              className="scm-file-input"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
            />
          </div>
        </div>

        <div className="scm-actions">
          <button
            type="button"
            className="scm-btn scm-btn-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            <i className="fa fa-ban me-1" />
            Cancel
          </button>
          <button
            type="button"
            className="scm-btn scm-btn-save"
            onClick={handleSubmit}
            disabled={!reason || isSaving}
            title={!reason ? "Reason is required" : "Save Change"}
          >
            <i
              className={`fa ${isSaving ? "fa-spinner fa-spin" : "fa-save"} me-1`}
            />
            {isSaving ? "Saving..." : "Save Change"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StatusChangeModal;
